/**
 * Shellback.ts
 * Shielded ground-patrol enemy for Starfall Courier — World 3+ (Brassclouds).
 *
 * Walks back and forth like Nibbler, but its front-facing shield blocks
 * incoming Star/Gem Shards. Vulnerable to hits from behind, to a stomp
 * from above, or to a sustained shard volley (HP > 1 from the front is
 * out of scope for the greybox stub — see ROADMAP Phase 4).
 *
 * State machine: PATROL → HURT → DEAD
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 3 (Brassclouds) stub. Wired into levels when W3 ships.
 * See ROADMAP.md Phase 4 (`Shellback.ts`).
 *
 * @module Shellback
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime } from '../../engine/gameState';
import { resolveAllPlatforms, hasFloorAhead } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type ShellbackState = 'PATROL' | 'HURT' | 'DEAD';

/** Width/height of the Shellback hitbox in logical pixels. */
export const SHELLBACK_WIDTH = 28;
export const SHELLBACK_HEIGHT = 26;

/** Walk speed in logical pixels per second — slower than Nibbler (heavy shell). */
const PATROL_SPEED = 40;

/** Duration of the hurt-flash before death in seconds. */
const HURT_DURATION = 0.25;

/** Render colour for greybox. */
export const SHELLBACK_COLOR = '#8a6a3a'; // brass-tan

// DECISION: Reusable edge/hitbox boxes to avoid per-tick allocations
// (mirrors Nibbler — same pattern, same performance budget).
const _hitbox = { x: 0, y: 0, width: SHELLBACK_WIDTH, height: SHELLBACK_HEIGHT };

/**
 * Result of a projectile impact decision. Exported so combat code can act
 * on a blocked hit (spark VFX, ricochet) separately from one that connects.
 *
 * DECISION: This union deliberately does not include a `'KILLED'` value.
 * `applyShardHit()` never sets `dead = true` synchronously — a rear hit
 * starts the HURT → DEAD sequence which completes inside `update()` once
 * the hurt timer elapses. Returning `'HURT'` keeps the return value
 * consistent with the post-call `state` so callers can’t mistake an
 * in-progress death animation for an already-dead enemy.
 */
export type ShellbackHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/**
 * Shellback — shielded ground-patrol enemy.
 *
 * Spawn with `new Shellback(x, y)`. Call `update(dt, level)` each fixed tick.
 * Read `dead` to know when to remove from the scene.
 */
export class Shellback extends BaseEntity {
  public readonly entityType = 'Shellback';
  public readonly color = SHELLBACK_COLOR;
  public state: ShellbackState = 'PATROL';

  /**
   * True once a hit has started the death sequence (`HURT`) or it has
   * finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'HURT' || this.state === 'DEAD';
  }
  /** True when facing right (used by renderer for sprite flip + shield side). */
  public facingRight = true;
  private _hurtTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, SHELLBACK_WIDTH, SHELLBACK_HEIGHT);
    this.vx = PATROL_SPEED; // start walking right
  }

  public update(dt: number, level: LevelRuntime): void {
    if (this.state === 'DEAD') return;

    if (this.state === 'HURT') {
      // DECISION: Hurt state freezes horizontal motion but keeps gravity
      // running so a Shellback hit while airborne still lands before the
      // death frame plays.
      this._hurtTimer -= dt;
      this.vy += GRAVITY * dt;
      this.vy = Math.min(this.vy, MAX_FALL_SPEED);
      this.y += this.vy * dt;
      _hitbox.x = this.x;
      _hitbox.y = this.y;
      const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
      // DECISION: Apply both axes from the resolver — vy can be non-zero in
      // HURT (gravity still runs), so corner/side overlaps can produce a
      // horizontal depenetration we must respect to avoid sticking inside
      // a platform during the death animation.
      this.x = r.x;
      this.y = r.y;
      if (r.landedOnTop) this.vy = 0;
      if (this._hurtTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('Shellback', 'Death after hurt');
      }
      return;
    }

    // ── Gravity ───────────────────────────────────────────────────────────────
    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    // ── Integrate ─────────────────────────────────────────────────────────────
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // ── Collision resolution ──────────────────────────────────────────────────
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const result = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = result.x;
    this.y = result.y;

    if (result.landedOnTop) {
      this.vy = 0;
      const hasFloor = hasFloorAhead(
        this.x,
        this.y,
        this.width,
        this.height,
        this.vx,
        level.platforms,
      );
      if (!hasFloor) {
        this.vx = -this.vx;
        this.facingRight = this.vx > 0;
        Logger.debug('Shellback', 'Turn at edge');
      }
    }

    if (result.hitLeft || result.hitRight) {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('Shellback', 'Turn at wall');
    }
  }

  /**
   * Apply one projectile hit. The shield blocks shards travelling
   * *into* the Shellback's front; shards travelling in the same
   * direction the Shellback faces are hitting it from behind and
   * connect.
   *
   * DECISION: The caller passes the projectile's horizontal velocity
   * sign rather than a boolean direction so the same call works for
   * Star Shards and Gem Shards (and any future variant) without a
   * new parameter type.
   *
   * A successful rear hit starts the HURT → DEAD sequence: the state
   * flips to `'HURT'` synchronously and `dead` becomes true only after
   * the hurt timer elapses inside `update()`. The return value matches
   * the synchronous state so callers can’t treat a HURT enemy as
   * already-dead.
   *
   * @param projectileVx - Horizontal velocity of the incoming shard
   *                       (positive = travelling right).
   * @returns 'BLOCKED' if shield absorbed, 'HURT' on a rear hit that
   *          started the death sequence, 'IGNORED' if already
   *          dead/hurt.
   */
  public applyShardHit(projectileVx: number): ShellbackHitResult {
    if (this.state === 'DEAD' || this.state === 'HURT') return 'IGNORED';
    const projectileGoingRight = projectileVx > 0;
    // Shellback faces right ⇒ shield is on the right ⇒ shard travelling
    // LEFT (vx < 0) is hitting the right-side shield from outside ⇒ block.
    const hitFromBehind = projectileGoingRight === this.facingRight;
    if (!hitFromBehind) {
      Logger.debug('Shellback', 'Shard blocked by shield');
      return 'BLOCKED';
    }
    this.state = 'HURT';
    this._hurtTimer = HURT_DURATION;
    this.vx = 0;
    Logger.debug('Shellback', 'Hit from behind — entering HURT');
    return 'HURT';
  }

  /**
   * Apply one stomp hit. The shell offers no protection from above —
   * Nova flattens the Shellback like any other ground enemy.
   * @returns true if killed by this hit (false if already dead/hurt).
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    this.state = 'HURT';
    this._hurtTimer = HURT_DURATION;
    this.vx = 0;
    Logger.debug('Shellback', 'Stomped');
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts) — delegates to
   * the specialised methods so `combatStep` can drive Shellback through the
   * shared enemy interface.
   *
   * DECISION: The shared `applyHit()` signature does not carry projectile
   * direction, so a shard hit through this adapter bypasses the directional
   * shield and connects directly. Callers that want shield evaluation must
   * use `applyShardHit(projectileVx)` directly. The directional shield will
   * be reinstated via the Enemy contract when `combatStep` is extended to
   * pass shard velocity (tracked in ROADMAP Phase 4).
   *
   * @param fromStomp `true` for stomp contact, `false`/omitted for shard hits.
   * @returns `true` if this hit started the death sequence; `false` otherwise.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    this.state = 'HURT';
    this._hurtTimer = HURT_DURATION;
    this.vx = 0;
    Logger.debug('Shellback', 'Hit via Enemy.applyHit — entering HURT');
    return true;
  }
}
